import { useEffect, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { useChatModeStoreActions as useChatModeStoreActionsRefresh } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeStoreActions';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { isTerminalTaskState } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import {
	useActiveTaskForConversation,
	useTasksStoreActions,
} from '@atlassian/conversation-assistant-store/controllers/unified-tasks';

import { useAssistanceServiceParams } from '../../common/utils/use-assistance-service-params';
import { getLastAgentMessageID } from '../chat-store/getLastAgentMessageID';
import { useChatStore } from '../chat-store/useChatStore';

type HookWithAssistanceServiceParams<P> = Omit<P, 'assistanceServiceInjectedParams'> &
	AssistanceServiceInputParam;

const useHydrateInner = ({
	assistanceServiceInjectedParams: { assistanceService },
}: AssistanceServiceInjectedParams): void => {
	// eslint-disable-next-line @atlaskit/platform/no-preconditioning -- enable_tasks is the umbrella gate for task updates.
	const enabled = fg('rovo_chat_enable_tasks');
	const { mountSSESubscriber, unmountSSESubscriber, setActiveConversationContext } =
		useTasksStoreActions();
	const [{ currentConversationId, conversations }] = useChatStore();
	const { setReasoningMode } = useChatModeStoreActionsRefresh();

	// Track the active task for the *current* conversation so we can react to
	// task lifecycle transitions (e.g. reset reasoning mode when task completes).
	const [activeTaskForCurrent] = useActiveTaskForConversation(currentConversationId ?? '');
	const previousActiveTaskRef = useRef<{ taskId: string | null; isTerminal: boolean }>({
		taskId: null,
		isTerminal: false,
	});

	// SSE lifecycle: open ref-counted SSE connection through the unified store.
	useEffect(() => {
		if (!enabled) {
			return undefined;
		}
		const getService = () => assistanceService;
		mountSSESubscriber(getService);
		return () => unmountSSESubscriber();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled]);

	// Keep the tasks store informed of the current conversation context for
	// SSE reconnect guards (replaces the idle-status + currentMessageId guards
	// that were in consumeTaskStatusUpdates).
	useEffect(() => {
		if (!enabled) {
			return;
		}
		const conversation = currentConversationId ? conversations[currentConversationId] : undefined;
		const lastMsgId = conversation
			? (getLastAgentMessageID(conversation.messages ?? []) ?? null)
			: null;
		setActiveConversationContext({
			conversationId: currentConversationId ?? null,
			lastAgentMessageId: lastMsgId,
			status: conversation?.status ?? null,
		});
	}, [currentConversationId, conversations, enabled, setActiveConversationContext]);

	// ─── Reasoning-mode auto-reset bridge ────────────────────────────────────
	//
	// Reset the reasoning mode to 'auto' when the *current conversation* has no
	// active in-flight task to drive task mode. Two distinct triggers:
	//
	//  1. Same-task completion: the active task transitions from non-terminal
	//     → terminal. Replaces the inline `setReasoningMode` side effect that
	//     lived inside the legacy `setTaskProgressState` chat-store action.
	//
	//  2. Conversation switch: when the user navigates to a conversation whose
	//     active task is in a TERMINAL state (i.e. there's stale "task mode"
	//     to clean up because a task previously completed in the background
	//     while the user was elsewhere). Conversations with NO task at all are
	//     intentionally excluded — a brand-new conversation may have its
	//     reasoning mode pre-set to 'task' by the UI in anticipation of a task
	//     the user is about to start, and clobbering that to 'auto' here would
	//     suppress the chat-input overlay until the BE catches up.
	const previousConversationIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (!enabled || !currentConversationId) {
			return;
		}

		const currentTaskId = activeTaskForCurrent?.taskId ?? null;
		const currentIsTerminal = activeTaskForCurrent
			? isTerminalTaskState(activeTaskForCurrent.progressState)
			: false;
		const previous = previousActiveTaskRef.current;
		const previousConversationId = previousConversationIdRef.current;
		const conversationJustSwitched =
			previousConversationId !== null && previousConversationId !== currentConversationId;

		const sameTaskCompletedNow =
			previous.taskId !== null &&
			previous.taskId === currentTaskId &&
			!previous.isTerminal &&
			currentIsTerminal;

		// Only fires when the new conversation has an EXISTING terminal task —
		// a brand-new conversation (no task) is intentionally excluded so the
		// user's pre-set 'task' reasoning mode is preserved. See bridge comment.
		const switchedToConversationWithStaleTerminalTask =
			conversationJustSwitched && activeTaskForCurrent !== undefined && currentIsTerminal;

		if (sameTaskCompletedNow || switchedToConversationWithStaleTerminalTask) {
			setReasoningMode(currentConversationId, 'auto');
		}

		previousActiveTaskRef.current = { taskId: currentTaskId, isTerminal: currentIsTerminal };
		previousConversationIdRef.current = currentConversationId;
	}, [activeTaskForCurrent, currentConversationId, enabled, setReasoningMode]);
};

/**
 * Hydrates the unified tasks store context.
 *
 * Responsibilities:
 * 1. Opens the Tasks SSE connection via the unified tasks store (ref-counted).
 * 2. Keeps the tasks store informed of the current conversation context so
 *    it can guard against stale SSE reconnect replays.
 *
 * Must be mounted globally (e.g. ConversationAssistantRender) so it is always
 * active when the chat is mounted, regardless of which panel is visible.
 *
 * Only active when fg('rovo_chat_enable_tasks') is true.
 */
const useHydrateInternal: (
	props: HookWithAssistanceServiceParams<AssistanceServiceInjectedParams>,
) => void = createHookWithAssistanceService(useHydrateInner);

/**
 * Wrapper that resolves assistanceServiceParams internally so call sites stay
 * argument-free.
 */
export const useHydrateTaskStoreContext = (): void => {
	const assistanceServiceParams = useAssistanceServiceParams({
		touchpointSource: 'rovo-tasks',
	});
	useHydrateInternal({ assistanceServiceParams });
};
